import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Sparkles,
  Sliders,
  Replace,
  SquarePlus,
  SquareMinus,
  Move,
  X
} from "lucide-react";
import { submitRevisionRequest } from "../services/LLMCalls"; //
import { getCurrentUserId } from "../services/database"; //
import RevisionSettings from "./RevisionSettings"; //
import RevisionHistory from "./RevisionHistory"; //
import ProvideReference from './ProvideReference'; //

function RevisionNotes({ viewId, versionId, projectId }) {
  // States for toggling sections
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  // States for text inputs in each category
  const [replaceText, setReplaceText] = useState("");
  const [addText, setAddText] = useState("");
  const [removeText, setRemoveText] = useState("");
  const [moveText, setMoveText] = useState("");
  const [adjustText, setAdjustText] = useState("");

  // States for storing saved instructions
  const [replaceRequests, setReplaceRequests] = useState([]);
  const [addRequests, setAddRequests] = useState([]);
  const [removeRequests, setRemoveRequests] = useState([]);
  const [moveRequests, setMoveRequests] = useState([]);
  const [adjustRequests, setAdjustRequests] = useState([]);

  // States for revision settings modal
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [revisionCount, setRevisionCount] = useState(0);
  const [revisionSettings, setRevisionSettings] = useState({
    differentModel: 'no',
    flexibility: 'low'
  });

  // State for history modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Modal open state
  const [showModal, setShowModal] = useState(false);
  const [reference, setReference] = useState(null); //

  // State for managing submission status of the "Proceed" button in the modal
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate(); //
  const location = useLocation(); //

  useEffect(() => {
    // Get the current view data from location state to count versions
    const { view } = location.state || {}; //
    if (view && view.versions) {
      setRevisionCount(view.versions.length); //
    }
  }, [location]);

  // Log revision count for debugging
  useEffect(() => {
    console.log("Current revision count:", revisionCount); //
  }, [revisionCount]);

  const noTransitionStyles = {
    overflow: "hidden",
    maxHeight: "0px",
    padding: "0"
  };

  const expandedStyles = {
    maxHeight: "9999px",
    padding: "1rem"
  };

  const getSectionStyles = (isOpen) => {
    return isOpen
      ? { ...noTransitionStyles, ...expandedStyles }
      : { ...noTransitionStyles, maxHeight: "0px", padding: "0" };
  };

  // Handlers for saving instructions
  const handleSaveReplace = () => {
    if (replaceText.trim()) {
      setReplaceRequests([...replaceRequests, replaceText.trim()]); //
      setReplaceText(""); //
    }
  };

  const handleSaveAdd = () => {
    if (addText.trim()) {
      setAddRequests([...addRequests, addText.trim()]); //
      setAddText(""); //
    }
  };

  const handleSaveRemove = () => {
    if (removeText.trim()) {
      setRemoveRequests([...removeRequests, removeText.trim()]); //
      setRemoveText(""); //
    }
  };

  const handleSaveAdjust = () => {
    if (adjustText.trim()) {
      setAdjustRequests([...adjustRequests, adjustText.trim()]); //
      setAdjustText(""); //
    }
  };

  // Handlers for Move box
  const handleSaveMove = () => {
    if (moveText.trim()) {
      setMoveRequests([...moveRequests, moveText.trim()]); //
      setMoveText(""); //
    }
  };

  const handleDeleteMove = (index) => {
    const newList = [...moveRequests]; //
    newList.splice(index, 1); //
    setMoveRequests(newList); //
  };

  // Handlers for deleting instructions
  const handleDeleteReplace = (index) => {
    const newList = [...replaceRequests]; //
    newList.splice(index, 1); //
    setReplaceRequests(newList); //
  };

  const handleDeleteAdd = (index) => {
    const newList = [...addRequests]; //
    newList.splice(index, 1); //
    setAddRequests(newList); //
  };

  const handleDeleteRemove = (index) => {
    const newList = [...removeRequests]; //
    newList.splice(index, 1); //
    setRemoveRequests(newList); //
  };

  const handleDeleteAdjust = (index) => {
    const newList = [...adjustRequests]; //
    newList.splice(index, 1); //
    setAdjustRequests(newList); //
  };

  const notesSummary = {
    replace: replaceRequests, //
    add: addRequests, //
    remove: removeRequests, //
    move: moveRequests, //
    adjust: adjustRequests, //
    differentModel: revisionSettings.differentModel, //
    flexibility: revisionSettings.flexibility, //
    reference //
  };

  const noInstructions =
    !reference && //
    !replaceText.trim() && //
    !addText.trim() && //
    !removeText.trim() && //
    !moveText.trim() && //
    !adjustText.trim() && //
    replaceRequests.length === 0 && //
    addRequests.length === 0 && //
    removeRequests.length === 0 && //
    moveRequests.length === 0 && //
    adjustRequests.length === 0; //

  // Handlers for the "Revise" button, opens the summary modal
  const handleReviseClick = () => {
    if (noInstructions) {
      alert("Please fill in at least one revision input or saved instruction before proceeding."); //
      return;
    }

    setShowModal(true); //
  };

  // Function to close the modal
  const closeModal = () => {
    if (isSubmitting) return; // Prevent closing if in the middle of a submission
    setShowModal(false); //
  };

  // Open settings modal
  const openSettingsModal = () => {
    setShowSettingsModal(true); //
  };

  // Close settings modal
  const closeSettingsModal = () => {
    setShowSettingsModal(false); //
  };

  // Open history modal
  const openHistoryModal = () => {
    setShowHistoryModal(true); //
  };

  // Close history modal
  const closeHistoryModal = () => {
    setShowHistoryModal(false); //
  };

  // Apply revision settings
  const applySettings = (settings) => {
    setRevisionSettings(settings); //
  };

  // Proceed with the revision request (from the modal)
  const handleProceed = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    setIsSubmitting(true);

    try {
      // Close the modal
      setShowModal(false); //

      const userId = await getCurrentUserId(); //

      // Pass the versionId here so the backend can revise the correct version
      const response = await submitRevisionRequest( //
        userId,
        projectId,
        viewId,
        versionId, // This is the sourceVersionId for the revision
        notesSummary
      );

      navigate("/prompt-status-3", { //
        state: {
          projectId,
          viewId,
          versionId: response.versionId, // This is the new versionId for the revision being processed
          isProcessing: true
        }
      });
    } catch (error) {
      console.error("Failed to submit revision request.", error); //
      alert("Error submitting revision request – please try again.");
    } finally {
      setIsSubmitting(false); // Reset submitting flag
    }
  };

  return (
    <div className="bg-white shadow rounded p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">Revision Notes</h2>
        <button
          onClick={openHistoryModal}
          className="text-black font-medium hover:underline"
        >
          View History
        </button>
      </div>

      {/* Replace Card */}
      <div className="bg-white shadow rounded mb-4">
        <div
          className="flex justify-between items-center p-4 cursor-pointer select-none"
          onClick={() => setReplaceOpen(!replaceOpen)}
        >
          <div className="flex items-center">
            <Replace className="text-gray-900 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Replace</h3>
          </div>
          {replaceOpen ? (
            <ChevronUp className="text-gray-900" />
          ) : (
            <ChevronDown className="text-gray-900" />
          )}
        </div>
        <div className="border-t border-gray-200" style={getSectionStyles(replaceOpen)}>
          {replaceRequests.map((item, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 text-gray-800 p-2 mb-2 rounded"
            >
              <button
                onClick={() => handleDeleteReplace(index)}
                className="mr-2 text-red-600"
              >
                <X size={16} />
              </button>
              <span>{item}</span>
            </div>
          ))}
          <textarea
            rows={4}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-700"
            placeholder={`Specify the element to replace and what should take its place.

[EXAMPLE] Replace 'Welcome' with 'Hello'`}
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
          />
          <div className="flex justify-end">
            <button
              onClick={handleSaveReplace}
              className="bg-gray-900 text-white py-1 px-3 rounded mt-2"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Add Card */}
      <div className="bg-white shadow rounded mb-4">
        <div
          className="flex justify-between items-center p-4 cursor-pointer select-none"
          onClick={() => setAddOpen(!addOpen)}
        >
          <div className="flex items-center">
            <SquarePlus className="text-gray-900 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Add</h3>
          </div>
          {addOpen ? (
            <ChevronUp className="text-gray-900" />
          ) : (
            <ChevronDown className="text-gray-900" />
          )}
        </div>
        <div className="border-t border-gray-200" style={getSectionStyles(addOpen)}>
          {addRequests.map((item, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 text-gray-800 p-2 mb-2 rounded"
            >
              <button
                onClick={() => handleDeleteAdd(index)}
                className="mr-2 text-red-600"
              >
                <X size={16} />
              </button>
              <span>{item}</span>
            </div>
          ))}
          <textarea
            rows={4}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-700"
            placeholder={`Describe what element to add and where it should appear.

[EXAMPLE] Add a FAQ section at the bottom`}
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
          />
          <div className="flex justify-end">
            <button
              onClick={handleSaveAdd}
              className="bg-gray-900 text-white py-1 px-3 rounded mt-2"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Remove Card */}
      <div className="bg-white shadow rounded mb-4">
        <div
          className="flex justify-between items-center p-4 cursor-pointer select-none"
          onClick={() => setRemoveOpen(!removeOpen)}
        >
          <div className="flex items-center">
            <SquareMinus className="text-gray-900 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Remove</h3>
          </div>
          {removeOpen ? (
            <ChevronUp className="text-gray-900" />
          ) : (
            <ChevronDown className="text-gray-900" />
          )}
        </div>
        <div className="border-t border-gray-200" style={getSectionStyles(removeOpen)}>
          {removeRequests.map((item, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 text-gray-800 p-2 mb-2 rounded"
            >
              <button
                onClick={() => handleDeleteRemove(index)}
                className="mr-2 text-red-600"
              >
                <X size={16} />
              </button>
              <span>{item}</span>
            </div>
          ))}
          <textarea
            rows={4}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-700"
            placeholder={`Indicate which element should be removed from the view.

[EXAMPLE] Remove the form from the sidebar`}
            value={removeText}
            onChange={(e) => setRemoveText(e.target.value)}
          />
          <div className="flex justify-end">
            <button
              onClick={handleSaveRemove}
              className="bg-gray-900 text-white py-1 px-3 rounded mt-2"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Move Card */}
      <div className="bg-white shadow rounded mb-4">
        <div
          className="flex justify-between items-center p-4 cursor-pointer select-none"
          onClick={() => setMoveOpen(!moveOpen)}
        >
          <div className="flex items-center">
            <Move className="text-gray-900 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Move</h3>
          </div>
          {moveOpen ? (
            <ChevronUp className="text-gray-900" />
          ) : (
            <ChevronDown className="text-gray-900" />
          )}
        </div>
        <div className="border-t border-gray-200" style={getSectionStyles(moveOpen)}>
          {moveRequests.map((item, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 text-gray-800 p-2 mb-2 rounded"
            >
              <button
                onClick={() => handleDeleteMove(index)}
                className="mr-2 text-red-600"
              >
                <X size={16} />
              </button>
              <span>{item}</span>
            </div>
          ))}
          <textarea
            rows={4}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-700"
            placeholder={`Describe what element to move and where.

[EXAMPLE] Move the submit button below the image.`}
            value={moveText}
            onChange={(e) => setMoveText(e.target.value)}
          />
          <div className="flex justify-end">
            <button
              onClick={handleSaveMove}
              className="bg-gray-900 text-white py-1 px-3 rounded mt-2"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Adjust Card */}
      <div className="bg-white shadow rounded mb-4">
        <div
          className="flex justify-between items-center p-4 cursor-pointer select-none"
          onClick={() => setAdjustOpen(!adjustOpen)}
        >
          <div className="flex items-center">
            <Sliders className="text-gray-900 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Adjust</h3>
          </div>
          {adjustOpen ? (
            <ChevronUp className="text-gray-900" />
          ) : (
            <ChevronDown className="text-gray-900" />
          )}
        </div>
        <div className="border-t border-gray-200" style={getSectionStyles(adjustOpen)}>
          {adjustRequests.map((item, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 text-gray-800 p-2 mb-2 rounded"
            >
              <button
                onClick={() => handleDeleteAdjust(index)}
                className="mr-2 text-red-600"
              >
                <X size={16} />
              </button>
              <span>{item}</span>
            </div>
          ))}
          <textarea
            rows={4}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-700"
            placeholder={`Specify adjustments to layout, styles, content or interactivity.

[EXAMPLE] Left-align the card title`}
            value={adjustText}
            onChange={(e) => setAdjustText(e.target.value)}
          />
          <div className="flex justify-end">
            <button
              onClick={handleSaveAdjust}
              className="bg-gray-900 text-white py-1 px-3 rounded mt-2"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* ProvideReference above Revise button */}
      <div className="mt-2 text-center flex flex-col items-center gap-4">
        {/* reference link */}
        <ProvideReference
          projectId={projectId}
          onReferenceChange={setReference}
        />

        {/* Revise button exactly as before */}
        <button
          onClick={handleReviseClick}
          className="bg-gray-900 text-white py-2 px-6 rounded hover:bg-gray-700 active:bg-gray-500 inline-flex items-center justify-center"
        >
          <Sparkles className="mr-2" size={18} />
          Revise
        </button>

        {revisionCount >= 6 && (
          <button onClick={openSettingsModal} className="text-black font-medium underline text-sm mt-2 hover:text-gray-700">
            {revisionSettings.differentModel === 'yes' || revisionSettings.flexibility !== 'low'
              ? 'Revision Settings Applied'
              : 'Not happy with results? See your options.'}
          </button>
        )}
      </div>

      {/* Fixed Position Confirmation Modal with Scrollable Content */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-bold">Confirm Your Revisions</h2>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Reference block before other sections */}
              {reference && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <h3 className="font-semibold mb-1">Reference View</h3>
                  <p className="text-gray-700">
                    {reference.viewTitle} (version {reference.versionIndex})<br />
                    Instructions: {reference.explanation}
                  </p>
                </div>
              )}

              {notesSummary.replace.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <h3 className="font-semibold mb-1">Replace</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {notesSummary.replace.map((item, index) => (
                      <li key={index} className="break-words">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {notesSummary.add.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <h3 className="font-semibold mb-1">Add</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {notesSummary.add.map((item, index) => (
                      <li key={index} className="break-words">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {notesSummary.remove.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <h3 className="font-semibold mb-1">Remove</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {notesSummary.remove.map((item, index) => (
                      <li key={index} className="break-words">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {notesSummary.move.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <h3 className="font-semibold mb-1">Move</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {notesSummary.move.map((item, index) => (
                      <li key={index} className="break-words">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {notesSummary.adjust.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <h3 className="font-semibold mb-1">Adjust</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {notesSummary.adjust.map((item, index) => (
                      <li key={index} className="break-words">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Show revision settings if applied */}
              {(revisionSettings.differentModel === 'yes' || revisionSettings.flexibility !== 'low') && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                  <h3 className="font-semibold mb-1">Advanced Settings</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {revisionSettings.differentModel === 'yes' && (
                      <li>Using alternative model</li>
                    )}
                    {revisionSettings.flexibility !== 'low' && (
                      <li>Model flexibility: {revisionSettings.flexibility}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Fixed Footer with Action Buttons */}
            <div className="px-6 py-4 border-t flex justify-end space-x-4 bg-white">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
                onClick={handleProceed}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing…" : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <RevisionSettings
          onClose={closeSettingsModal}
          onApply={applySettings}
          defaultSettings={revisionSettings}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <RevisionHistory
          projectId={projectId}
          viewId={viewId}
          currentVersionId={versionId}
          onClose={closeHistoryModal}
        />
      )}
    </div>
  );
}

export default RevisionNotes;